import React, { useCallback } from 'react';
import { Dispatch, PropsWithChildren, SetStateAction, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import ThumbnailViewer from './ThumbnailViewer';
import JsonViewer from './JsonViewer';
import { palette } from 'styles/color';
import { unzip, ZipEntry } from 'unzipit';
import { apiWrapper } from 'api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppDispatch } from 'store/store';
import { activeToast } from 'store/slices/toastSlice';
import ga, { EventUploadCancel } from 'util/gaConnect';
import { JsonType, SampleType } from 'pages/Playground/DocExtractor';

type TaskIdType = string;
export type JsonResponseType = {
  json: any; // JSON 데이터
  images: Array<{
    fileName: string; // 이미지 파일명
    url: string; // Blob URL
  }>;
};
export type ImagesResponseType = { images: string[] };

export interface DataExtractorViewProps {
  file?: File;
  setFile: Dispatch<SetStateAction<File | undefined>>;
  selectedType?: SampleType;
  setSelectedType?: Dispatch<SetStateAction<SampleType>>;

  sampleData?: any;
  clearSampleData?: () => void;
  schemaType: JsonType;
  setSchemaType: Dispatch<SetStateAction<JsonType>>;
}

export type pageIndexType = { index: number };
const THUMBNAIL_WIDTH = 900;
const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

const SUPPORTED_EXTENSIONS = ['docx', 'pptx', 'xlsx', 'hwp', 'hwpx'];

export default function DataExtractorViewer(props: PropsWithChildren<DataExtractorViewProps>) {
  const { t } = useTranslation();
  const {
    file,
    setFile,
    sampleData,
    clearSampleData,
    selectedType,
    schemaType,
    setSchemaType,
    setSelectedType
  } = props;
  const dispatch = useAppDispatch();
  const [pageIndex, setPageIndex] = useState<pageIndexType>({ index: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const objRef = useRef<HTMLDivElement>(null);
  // const abort = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [resFileBlob, setResFileBlob] = useState<Blob>();

  const [docExtension, setDocExtension] = useState<string | undefined>(undefined);
  const [uploadNonce, setUploadNonce] = useState(0);
  const isAbortRef = useRef(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!file) {
      queryClient.removeQueries({ queryKey: ['docExtractor'] });
    }
  }, [file, queryClient]);

  const clearDataAndResetPage = useCallback(() => {
    if (clearSampleData) {
      clearSampleData();
    }
    setPageIndex({ index: 0 });
    setUploadNonce(0);
  }, [clearSampleData, setPageIndex]);

  const fetchFileExtension = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      // formData.append('fileName', 'chart_example.hwp');
      // formData.append('fileName', '행203열101.pptx');

      if (file.size === 0) {
        throw new Error(`${file?.name}\n${t('toast.processingFailed')}\n${t('toast.uploadRetry')}`);
      }

      // AbortController 생성
      abortControllerRef.current = new AbortController();

      const response = await fetch('/api/v1/datainsight/playground/doc-extension', {
        method: 'POST',
        body: formData,
        signal: abortControllerRef.current.signal
      });

      // Max size 넘는 문서 업로드 시도했을 경우
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(t('toast.fileTooLarge'));
      }

      const data = await response.json();
      const errorCode = data.error?.code;

      if (errorCode === '108') {
        throw new Error(t('toast.fileNameTooLong'));
      }
      const extension = data?.data?.cleanedExtension;

      if (!SUPPORTED_EXTENSIONS.includes(extension)) {
        throw new Error(t('toast.unsupportedFileType'));
      }

      return extension;
    } catch (error: any) {
      dispatch(
        activeToast({
          type: 'error',
          msg: error.message,
          infinity: true
        })
      );
      setDocExtension(undefined);
      setFile(undefined);
      // setUploadNonce((prev) => prev + 1);

      return undefined;
    }
  };

  useEffect(() => {
    if (selectedType !== 'upload') {
      setDocExtension(undefined); // selectedType이 upload가 아니면 docExtension 초기화
    }
  }, [selectedType]);

  useEffect(() => {
    const getFileExtension = async () => {
      if (file) {
        const extension = await fetchFileExtension(file);
        setDocExtension(() => extension);
      }
    };

    getFileExtension();
  }, [file]);

  const reqExtractDocument = async (file: File): Promise<TaskIdType> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('option', 'png');
      formData.append('fileExtension', docExtension || '');

      // NOTE: 임시 처리
      // formData.append('fileName', '0012012-4_제안요청서 및 추진계획서.docx');
      // formData.append('fileName', '경영평가 측정 방법 (1).docx');
      // formData.append('fileName', '102539XML시장동향과전망.docx');
      // formData.append('fileName', 'text_onlFy.sample.docx'); //timeout error
      // formData.append('fileName', '요약출장보고서.hwp');
      // formData.append('fileName', '요약출장보고서.hwp');
      // formData.append('fileName', '2012 R&E 연구과제 발표.pptx');
      // formData.append('fileName', '201004_2010생명의숲_워크숍.pptx');
      // formData.append('fileName', '2011 인계초 급식 설문지(학부모) 결과.pptx');
      // formData.append('fileName', 'pdf-sample1.pdf');
      // formData.append('fileName', '변액1206.xls');
      // formData.append('fileName', '산출표.xls');
      // formData.append('fileName', 'xls-sample1.xls');
      // formData.append('fileName', 'xls-sample2.xls');
      // formData.append('fileName', '퀸시에너지절감비교자료.xlsx');
      // formData.append('fileName', 'table_example.xlsx');
      // formData.append('fileName', 'chart_example.hwp');
      // formData.append('fileName', '행203열101.pptx');
      // formData.append('fileName', 'pdf-sample1.pdf');
      // formData.append('fileName', '차트 총 2개(1페이지 2개).hwp');
      // formData.append('fileName', '2.과업지시서(전자문서 기능개선).hwp');
      // formData.append('fileName', '이미지 총 5개(Sheet1 5개) (1).xlsx');
      // formData.append('fileName', '열기암호.docx');
      // formData.append('fileName', 'rebuild 3.docx');
      // formData.append('fileName', '데이터.xlsx');
      // formData.append('fileName', '25년 종합검진 항목표_한신메디피아ㅇ (직원).xlsx');
      // formData.append('fileName', '20110723아점프_캠프제안서.pptx');
      // formData.append(
      //   'fileName',
      //   '[322]I3001_N_산업재해미보고 관련 과태료 부과현황보고(유통용)ㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇㅁㄴㅇ.hwp'
      // );

      const res = await apiWrapper('/api/v1/datainsight/playground/doc-extract', {
        method: 'POST',
        body: formData,
        signal: abortControllerRef.current?.signal
      });
      const json = await res.json();

      const taskId = json.data.taskId;
      return taskId;
    } catch (err) {
      if (typeof err === 'object' && err !== null && 'isAbort' in err) {
        isAbortRef.current = true;
        throw err;
      }

      throw new Error();
    }
  };

  const pollingExtractStatus = async (taskId: TaskIdType): Promise<boolean> => {
    const checkExtractStatus = async (taskId: TaskIdType) => {
      try {
        const res = await apiWrapper(
          `/api/v1/datainsight/playground/doc-extract/status/${taskId}`,
          {
            signal: abortControllerRef.current?.signal
          }
        );
        const json = await res.json();
        const status = json.data.status;
        const code = json.data.code;
        return {
          status,
          code
        };
        // return status;
      } catch (err) {
        if (typeof err === 'object' && err !== null && 'isAbort' in err) {
          isAbortRef.current = true;
          throw err;
        }
        throw new Error();
      }
    };

    return new Promise((resolve) => {
      setTimeout(async () => {
        try {
          // if (abort.current) {
          //   resolve(false);
          //   return;
          // }
          const { status, code } = await checkExtractStatus(taskId);
          if (status === 'done') {
            resolve(true);
          } else if (status === 'processing') {
            resolve(await pollingExtractStatus(taskId));
          } else if (status === 'failed') {
            if (code === '105') {
              // 암호 파일 업로드 시도 시
              dispatch(
                activeToast({
                  type: 'error',
                  msg: `${t('toast.fileEncrypted')}`
                })
              );
            } else if (code === '107') {
              // 처리 불가한 파일 업로드
              dispatch(
                activeToast({
                  type: 'error',
                  msg: `${t('toast.fileNotProcessable')}`
                })
              );
            } else {
              // 파일 업로드 호출 실패 or 에러
              dispatch(
                activeToast({
                  type: 'error',
                  msg: `${file?.name}\n${t('toast.processingFailed')}\n${t('toast.uploadRetry')}`,
                  infinity: true
                })
              );
            }

            // setUploadNonce((prev) => prev + 1);
            // resolve(false);
            setFile(undefined);
            setDocExtension(undefined);
            return;
          } else {
            resolve(false);
          }
        } catch (e) {
          resolve(false);
        }
      }, 1000);
    });
  };

  const getResultExtractDocument = async (taskId: TaskIdType) => {
    try {
      const res = await apiWrapper(`/api/v1/datainsight/playground/doc-extract/result/${taskId}`, {
        signal: abortControllerRef.current?.signal
      });
      const blob = await res.blob();
      const zip = await unzip(blob);
      const dataEntry = zip.entries['data.zip'];
      const imageEntry = zip.entries['image.zip'];
      if (!dataEntry || !imageEntry) throw new Error();

      const extractDataEntry = (entry: ZipEntry): Promise<JsonResponseType> => {
        return new Promise(async (resolve, reject) => {
          const blob = await entry.blob();
          setResFileBlob(blob);
          const data = await unzip(blob);

          // json 데이터 추출
          const jsonEntry = Object.values(data.entries).find((entry) =>
            entry.name.endsWith('.json')
          );
          if (!jsonEntry) reject();
          const json = JSON.parse(await jsonEntry!.text());

          // 이미지 데이터 추출
          const imageEntries = Object.values(data.entries).filter(
            (entry) => entry.name.match(/\.(png|jpg|jpeg|gif)$/i) // 이미지 파일 필터링
          );

          const images = await Promise.all(
            imageEntries.map(async (entry) => {
              const fileName = entry.name; // 파일명
              const blob = await entry.blob(); // Blob 생성
              const correctedBlob = new Blob([blob], { type: 'image/png' }); // MIME 타입 강제 설정
              const url = URL.createObjectURL(correctedBlob);

              return { fileName, url };
            })
          );

          // const images = await Promise.all(
          //   imageEntries.map(async (entry) => {
          //     const fileName = entry.name;
          //     const blob = await entry.blob();

          //     const base64 = await new Promise<string>((resolve, reject) => {
          //       const reader = new FileReader();
          //       reader.onloadend = () => resolve(reader.result as string);
          //       reader.onerror = reject;
          //       reader.readAsDataURL(blob);
          //     });

          //     return { fileName, url: base64 };
          //   })
          // );

          resolve({
            json, // JSON 데이터
            images // 이미지 데이터
          });
        });
      };

      const extractImageEntry = (entry: ZipEntry): Promise<ImagesResponseType> => {
        return new Promise(async (resolve, reject) => {
          const blob = await entry.blob();
          const image = await unzip(blob);

          const response = await Promise.all(
            Object.values(image.entries).map((entry) => {
              const index = parseInt(entry.name.split('_')[1].split('.')[0]);

              return new Promise<{ index: number; content: string }>(async (resolve) => {
                const blob = await entry.blob();
                const reader = new FileReader();
                reader.onloadend = (e) => {
                  resolve({ index, content: e.target?.result as string });
                };
                reader.onerror = (e) => {
                  reject(e);
                };
                reader.readAsDataURL(blob);
              });
            })
          );
          const images = response.sort((a, b) => a.index - b.index);

          resolve({
            images: images.map((image) => image.content)
          });
        });
      };

      const [dataResult, imageResult] = await Promise.all([
        extractDataEntry(dataEntry),
        extractImageEntry(imageEntry)
      ]);

      return {
        json: dataResult,
        images: imageResult.images
      };
    } catch (err) {
      if (typeof err === 'object' && err !== null && 'isAbort' in err) {
        isAbortRef.current = true;
        throw err;
      }
      throw new Error();
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: [`docExtractor`, uploadNonce],
    queryFn: async () => {
      try {
        if (!file || file.size > MAX_FILE_SIZE || !docExtension) {
          return undefined;
        }

        if (file) {
          const taskId = await reqExtractDocument(file);
          const success = await pollingExtractStatus(taskId);
          if (!success) throw new Error();
          const result = await getResultExtractDocument(taskId);

          return result;
        } else {
          return undefined;
        }
      } catch (e) {
        if (isAbortRef.current) {
          dispatch(
            activeToast({
              type: 'info',
              msg: t('toast.uploadCancel')
            })
          );
          isAbortRef.current = false;
          return undefined;
        } else {
          dispatch(
            activeToast({
              type: 'error',
              msg: `${file?.name}\n${t('toast.processingFailed')}\n${t('toast.uploadRetry')}`,
              infinity: true
            })
          );
        }

        return undefined;
      }
    },
    enabled: !!file && file.size <= MAX_FILE_SIZE && !!docExtension && uploadNonce > 0
  });

  useEffect(() => {
    if (file) {
      setUploadNonce(Date.now());
    }
  }, [file]);

  // useEffect(() => {
  //   if (file && file.size > MAX_FILE_SIZE) {
  //     dispatch(
  //       activeToast({
  //         type: 'error',
  //         msg: `${t('toast.fileTooLarge')}`
  //       })
  //     );
  //     setFile(undefined); // 파일 초기화
  //   }
  // }, [file, dispatch, t, setFile]);

  const onCancelExtract = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // setIsAbort(true); // abort 상태 기록

    setFile(undefined);
    setUploadNonce(0);
    setDocExtension(undefined);

    // 쿼리 캐시 초기화
    queryClient.removeQueries({ queryKey: ['docExtractor'] });

    ga.event(EventUploadCancel);
  };

  return (
    <DataExtractorViewerBase>
      <div className="thumbnailViewerArea">
        <ThumbnailViewer
          thumbnails={data?.images || sampleData?.pageimages}
          data={data?.json.json || sampleData?.json}
          isLoading={isLoading}
          file={file}
          docExtension={docExtension}
          sampleData={sampleData}
          clearSampleData={clearDataAndResetPage}
          setFile={setFile}
          pageIndex={pageIndex}
          onChangePageIndex={setPageIndex}
          width={THUMBNAIL_WIDTH}
          ref={ref}
          objRef={objRef}
          onCancelExtract={onCancelExtract}
          selectedType={selectedType}
          schemaType={schemaType}
          setSchemaType={setSchemaType}
          setSelectedType={setSelectedType}></ThumbnailViewer>
      </div>
      <div className="jsonViewerArea">
        <JsonViewer
          data={data?.json.json || sampleData?.json}
          sampleData={sampleData?.json}
          imgData={data?.json.images}
          fullJson={sampleData?.fullJson}
          selectedType={selectedType}
          isLoading={isLoading}
          file={file}
          docExtension={docExtension}
          resFileBlob={resFileBlob}
          setFile={setFile}
          pageIndex={pageIndex}
          onChangePageIndex={setPageIndex}
          highlight={{ ref, width: THUMBNAIL_WIDTH }}
          schemaType={schemaType}
          setSchemaType={setSchemaType}></JsonViewer>
      </div>
    </DataExtractorViewerBase>
  );
}

const DataExtractorViewerBase = styled.div`
  width: 100%;
  height: 1061px;
  display: flex;
  flex-direction: row;

  border: solid 1px ${palette.gray[200]};
  border-radius: 10px;
  overflow-x: auto;

  .thumbnailViewerArea {
    height: 100%;
    flex: 1 1 0;
    min-width: 0;
  }

  .jsonViewerArea {
    height: 100%;
    flex: 0 0 520px;
    width: 520px;
  }
`;
